import {
  TextComponent,
  TextComponentProps,
} from '@manifest-cyber/design-system/lib/components/TextComponent';
import { Box, Flex, Skeleton, ScrollArea } from '@mantine/core';
import { Text } from '@mantine/core';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ManifestModelSearchDropdown.module.scss';
import Icon from '../Icon';
import ClickableRegion from '../ClickableRegion';
import { AtomIcon } from '../Icons/AtomIcon/AtomIcon';
import { Tooltip } from '@mantine/core';
import { ActionableCard } from '../ActionableCard/ActionableCard';
import {
  GetAiModelQuickSearchResponseData,
  GetAiModelTrendingResponseData,
  PostAiModelAnalysisRequestBody,
} from '@manifest-cyber/types/interface/contracts/model-analysis';

export type ManifestModelSearchDropdownScenario =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'no-results'; query: string }
  | { kind: 'results'; items: GetAiModelQuickSearchResponseData }
  | { kind: 'trending'; items: GetAiModelTrendingResponseData };

export type ManifestModelSearchDropdownProps = {
  scenario: ManifestModelSearchDropdownScenario;
  onSelect: (m: PostAiModelAnalysisRequestBody) => void;
};

export type ManifestModelSearchDropdownResultItemProps = {
  title: string;
  subtitle: string;
  icon: string;
  kind: 'model' | 'dataset';
};

const TruncatedText = ({ text, maxLength }: { text: string; maxLength: number }) => {
  const shouldTruncate = text?.length > 0 && text?.length > maxLength;
  if (shouldTruncate) {
    return (
      <Tooltip label={text}>
        <span>{text.slice(0, maxLength) + '...'}</span>
      </Tooltip>
    );
  }
  return text;
};

const Boxed = ({ children }: { children: ReactNode }) => {
  return (
    <Box className={styles.manifestModelSearchDropdown}>
      <ScrollArea.Autosize placeholder={''} type="auto" scrollbarSize={8}>
        {children}
      </ScrollArea.Autosize>
    </Box>
  );
};

export function ManifestModelSearchDropdown(props: ManifestModelSearchDropdownProps) {
  const { scenario, onSelect } = props;
  return (
    <Boxed>
      {scenario.kind === 'loading' && <ManifestModelSearchLoading />}
      {scenario.kind === 'trending' && (
        <ManifestModelSearchTrending items={scenario.items} onSelect={onSelect} />
      )}
      {scenario.kind === 'error' && <ManifestModelSearchError />}
      {scenario.kind === 'no-results' && (
        <ManifestModelSearchNoResults query={scenario.query} />
      )}
      {scenario.kind === 'results' && (
        <ManifestModelSearchResults items={scenario.items} onSelect={onSelect} />
      )}
    </Boxed>
  );
}

function ManifestModelSearchLoading() {
  return (
    <Flex align="center" justify="center" w="100%" p={4} rowGap={4} direction={'column'}>
      <Skeleton height={40} />
      <Skeleton height={40} />
    </Flex>
  );
}

function ManifestModelSearchError() {
  const { t } = useTranslation();
  return (
    <Flex justify="center" align="center" h="100%">
      <ActionableCard
        title={t('error.general.errorLoadingData')}
        subtitle={t('error.general.errorLoadingDataSubtitle')}
        Icon={<Icon icon="circle-exclamation" size="sm" />}
      />
    </Flex>
  );
}

function ManifestModelSearchNoResults({ query }: { query: string }) {
  const { t } = useTranslation();
  return (
    <Flex align="center" justify="center" w="100%" p={96}>
      <TextComponent color="muted" variant="bodySmall">
        {t('search.notFoundResults')}: "{query}"
      </TextComponent>
    </Flex>
  );
}

function ManifestModelSearchResults({
  items,
  onSelect,
}: {
  items: GetAiModelQuickSearchResponseData;
  onSelect: (m: PostAiModelAnalysisRequestBody) => void;
}) {
  return (
    <Flex align="left" justify="left" w="100%" p={1} direction={'column'}>
      {items.map((item, i) => {
        const title = `${item.owner}/${item.repo}`;
        return (
          <Item
            key={`${item.owner}/${item.repo}/${item.revision}-${i}`}
            title={title}
            onClick={() =>
              onSelect({
                kind: item.kind,
                repo: item.repo,
                owner: item.owner,
                revision: item.revision,
              })
            }
          />
        );
      })}
    </Flex>
  );
}

const Item = ({
  title,
  subtitle,
  onClick,
  isLoading = false,
}: {
  title: string;
  subtitle?: string;
  iconColor?: TextComponentProps['color'];
  onClick?: () => void;
  isLoading?: boolean;
}) => {
  return (
    <Box component="li" w={'100%'}>
      <ClickableRegion
        disabled={isLoading}
        onClick={onClick}
        className={styles.resultItem}
      >
        <Flex justify="space-between" p={10} gap={10}>
          <Flex align="center" gap={10}>
            <Flex direction="column" gap={2} justify={'flex-start'} align={'flex-start'}>
              <Skeleton h="auto" w="auto" visible={isLoading}>
                <Flex align="center" gap={8} direction="row">
                  <div className={styles.resultItemIcon}>
                    <AtomIcon width={16} height={16} />
                  </div>
                  <TextComponent
                    variant="headerMedium"
                    color="base"
                    lineClamp={2}
                    className={styles.resultItemTitle}
                  >
                    <TruncatedText text={title} maxLength={50} />
                  </TextComponent>
                </Flex>
              </Skeleton>

              <Skeleton h="auto" w="auto" visible={isLoading}>
                {subtitle && (
                  <TextComponent variant="bodyMedium" color="muted" pl={24}>
                    <TruncatedText text={subtitle} maxLength={50} />
                  </TextComponent>
                )}
              </Skeleton>
            </Flex>
          </Flex>
        </Flex>
      </ClickableRegion>
    </Box>
  );
};

function ManifestModelSearchTrending({
  items,
  onSelect,
}: {
  items: GetAiModelTrendingResponseData;
  onSelect: (m: PostAiModelAnalysisRequestBody) => void;
}) {
  const { t } = useTranslation();
  return (
    <Flex align="left" justify="left" w="100%" p={1} direction={'column'}>
      <TextComponent variant="headerAllCaps" color="muted" p={10} transform="uppercase">
        {t('page.ai-explore.mostPopularModels')}
      </TextComponent>
      {items.map((item, i) => {
        const title = `${item.owner}/${item.repo}`;
        return (
          <Item
            key={`${item.owner}/${item.repo}/${item.revision}-${i}`}
            title={title}
            subtitle={item.type}
            onClick={() =>
              onSelect({
                kind: item.kind,
                repo: item.repo,
                owner: item.owner,
                revision: item.revision,
              })
            }
          />
        );
      })}
    </Flex>
  );
}
